<template>
  <q-form
    @submit="saveRecord"
    class="q-gutter-md"
  >
    <div v-if="adminMode === 'Add User' || adminMode === 'Update User'" style="display: flex; alignItems: baseline">
      <div class="inputLabel">Email</div>
      <q-space />
      <q-input
        type="email"
        autofocus
        v-model="email"
        v-if="adminMode === 'Add User'"
        outlined
        lazy-rules
        :rules="[
          (val) => (val && val.length > 0) || 'Required ',
          isValidEmail,
        ]"
      />
      <span v-else style="width: 50%; margin-bottom: 30px;">{{email}}</span>
    </div>

    <div v-if="adminMode === 'Add User' || adminMode === 'Update User' || adminMode === 'Add User Group' || adminMode === 'Update User Group'" style="display: flex; alignItems: baseline">
      <div class="inputLabel">Name</div>
      <q-space />
      <q-input
        v-model="name"
        outlined
        lazy-rules
        :rules="[
          (val) => (val && val.length > 0) || 'Required ',
          isValidName,
        ]"
      />
    </div>

    <div style="display: flex; alignItems: baseline">
      <div class="inputLabel">Access Level</div>
      <q-space />
      <span style="width: 50%;">
        <q-checkbox v-model="accessLevel" val="API" label="API" />
        <q-checkbox v-if="displayAdminAccessLevel" v-model="accessLevel" val="Admin" label="Admin" />
        <q-checkbox v-model="accessLevel" val="Delete" label="Delete Documents" />
        <q-checkbox v-model="accessLevel" val="Edit" label="Edit Documents" />
        <q-checkbox v-model="accessLevel" val="Upload" label="Upload Documents" />
        <q-checkbox v-model="accessLevel" val="User_Group_Access" label="User Group Access" />
      </span>
    </div>

    <div style="display: flex; alignItems: baseline">
      <div class="inputLabel">Scopes Access</div>
      <q-space />
      <span style="width: 50%;">
        <q-checkbox v-model="scopesAccess" val="Factrak" label="Factrak" />
        <q-checkbox v-model="scopesAccess" val="Fire Safety" label="Fire Safety" />
        <q-checkbox v-model="scopesAccess" val="HIDOE Drawings" label="HIDOE Drawings" />
        <q-checkbox v-model="scopesAccess" val="HIDOE Drawings Light" label="HIDOE Drawings Light" />
        <q-checkbox v-model="scopesAccess" val="Maui Fire Claims 2023" label="Maui Fire Claims 2023" />
      </span>
    </div>

    <div v-if="adminMode === 'Add User' || adminMode === 'Update User'" style="display: flex; alignItems: baseline">
      <div class="inputLabel">User Groups</div>
      <q-space />
      <q-select
        outlined
        v-model="userGroups"
        use-input
        use-chips
        multiple
        input-debounce="0"
        :options="userGroupsOptions"
        @filter="filterUserGroups"
      />
    </div>

    <div v-if="adminMode === 'Add User Group' || adminMode === 'Update User Group'" style="display: flex; alignItems: baseline">
      <div class="inputLabel">Users</div>
      <q-space />
      <q-select
        outlined
        v-model="users"
        use-input
        use-chips
        multiple
        input-debounce="0"
        :options="usersOptions"
        @filter="filterUsers"
      />
    </div>

    <div v-if="adminMode === 'Update User'" style="display: flex; alignItems: baseline">
      <div class="inputLabel">Authentication Token</div>
      <q-space />
      <span style="width: 50%;">
        <span class="q-gutter-x-sm">
          <q-btn
            label="Generate Token"
            color="primary"
            no-caps
            @click="generateAuthenticationToken"
            :disable="disabledFields"
          />
          <q-btn
            round
            flat
            @click="copyAuthenticationTokenToClipboard()"
            icon="content_copy"
            v-if="authenticationToken"
          />
          <span>{{authenticationToken}}</span>
        </span>
      </span>
    </div>

    <br />

    <div style="display: flex; alignItems: baseline">
      <q-space />
      <q-btn
        :label="adminMode"
        type="submit"
        color="primary"
        no-caps
      />
      <q-btn
        label="Cancel"
        @click="closeForm"
        color="cancelButton"
        class="q-ml-sm"
        no-caps
      />
      <q-space />
    </div>
  </q-form>
</template>

<script>
import { computed, ref } from "vue";
import axios from "axios";
import CommonHelper from "../helper/CommonHelper.js";
import NotificationHelper from "../helper/NotificationHelper";
import EmailService from "../service/EmailService.js";

export default {
  name: "AdminForm",
  props: {
    currentUserUID: String,
    ipAddress: String,
    adminMode: String,
    usersTable: Object,
    userGroupsTable: Object
  },
  emits: ["setAdminMode"],
  setup(props, { emit }) {
    const displayAdminAccessLevel = computed(() => {
      return props.currentUserUID === process.env.VUE_APP_DEFAULT_USER_UID;
    });

    const userGroupUid = ref(""); // user groups
    const originalName = ref(""); // user groups
    const email = ref(""); // users
    const name = ref("");
    const accessLevel = ref([]);
    const scopesAccess = ref([]);
    const userGroups = ref([]); // users
    const users = ref([]); // user groups
    const authenticationToken = ref(""); // users

    /*
     * Dictionary and ref containing the options for the User Groups dropdown
     */
    const userGroupsOptionsDictionary = computed(() => {
      let returnValue = {};

      props.userGroupsTable.rowData.forEach((row) => {
        returnValue[row.name] = row.userGroupUid;
      })

      return returnValue;
    });
    const userGroupsOptions = ref();

    const userGroupUidList = computed(() => {
      return userGroups.value.map((key) => {
        return userGroupsOptionsDictionary.value[key];
      });
    });

    /*
     * Filter function for the User Groups dropdown
     */
    function filterUserGroups(val, update) {
      CommonHelper.filterOptions(val, update, userGroupsOptions, Object.keys(userGroupsOptionsDictionary.value));
    }

    /*
     * Dictionary and ref containing the options for the Users dropdown
     */
    const usersOptionsDictionary = computed(() => {
      let returnValue = {};

      props.usersTable.rowData.forEach((row) => {
        let key = `${row.name} (${row.email})`

        returnValue[key] = row.email;
      })

      return returnValue;
    });
    const usersOptions = ref();

    const emails = computed(() => {
      return users.value.map((key) => {
        return usersOptionsDictionary.value[key];
      });
    });

    /*
     * Filter function for the Users dropdown
     */
    function filterUsers(val, update) {
      CommonHelper.filterOptions(val, update, usersOptions, Object.keys(usersOptionsDictionary.value));
    }

    const disabledFields = ref(false);

    function addRow() {
      userGroupUid.value = "";
      originalName.value = "";
      email.value = "";
      name.value = "";
      accessLevel.value = [];
      scopesAccess.value = [];
      userGroups.value = [];
      users.value = [];
    }

    function editRow(prop) {
      const editRowValue = prop.row;
      userGroupUid.value = editRowValue.userGroupUid;
      originalName.value = editRowValue.name;
      email.value = editRowValue.email;
      name.value = editRowValue.name;
      accessLevel.value = editRowValue.access !== "" ? editRowValue.access.split(", ") : [];
      scopesAccess.value = editRowValue.scopes !== "" ? editRowValue.scopes.split(", ") : [];
      userGroups.value = editRowValue.user_groups !== undefined && editRowValue.user_groups !== "" ? editRowValue.user_groups.split(":::") : [];
      authenticationToken.value = editRowValue.authenticationToken;

      if (editRowValue.emails !== undefined && editRowValue.emails !== "") {
        users.value = editRowValue.emails.split(",").map((email) => {
          let returnValue = "";

          props.usersTable.rowData.forEach((row) => {
            if (row.email === email) {
              returnValue = `${row.name} (${row.email})`;
            }
          })

          return returnValue;
        });
        users.value.sort();
      } else {
        users.value = [];
      }
    }

    function isValidEmail() {
      const emailPattern =
        /^(?=[a-zA-Z0-9@._%+-]{6,254}$)[a-zA-Z0-9._%+-]{1,64}@(?:[a-zA-Z0-9-]{1,63}\.){1,8}[a-zA-Z]{2,63}$/;
      return (emailPattern.test(email.value) && !isDuplicateEmail(email.value)) || "Invalid email";
    }

    /*
     * Returns true if email exists in props.usersTable.rowData.email; Function used in isValidEmail
     */
    function isDuplicateEmail() {
      let duplicateBool = false;
      props.usersTable.rowData.forEach((row) => {
        if (row.email.toLowerCase().trim() === email.value.toLowerCase().trim()) {
          duplicateBool = true;
        }
      })
      return duplicateBool;
    }

    function isValidName() {
      return (props.adminMode === "Add User" || props.adminMode === "Update User" || !isDuplicateName()) || "Duplicate name";
    }

    /*
     * Returns true if name exists in props.userGroupsTable.rowData.name; Function used in isValidName
     */
    function isDuplicateName() {
      let duplicateBool = false;
      if (originalName.value.toLowerCase().trim() !== name.value.toLowerCase().trim()) {
        props.userGroupsTable.rowData.forEach((row) => {
          if (row.name.toLowerCase().trim() === name.value.toLowerCase().trim()) {
            duplicateBool = true;
          }
        })
      }
      return duplicateBool;
    }

    async function generateAuthenticationToken() {
      try {
        disabledFields.value = true;
        const response = await axios.get(`${process.env.VUE_APP_API_GATEWAY}generateAuthenticationToken?emailAddress=${email.value}&currentUserUID=${props.currentUserUID}&ipAddress=${props.ipAddress}&key=${process.env.VUE_APP_API_KEY}`);
        authenticationToken.value = response.data;
        props.usersTable.getUsers();
        disabledFields.value = false;
      } catch (error) {
        EmailService.sendErrorEmail("AdminForm.vue - generateAuthenticationToken", error);
        disabledFields.value = false;
        NotificationHelper.createErrorNotification("Error encountered. Please try again.");
      }
    }

    async function copyAuthenticationTokenToClipboard() {
      navigator.clipboard.writeText(authenticationToken.value);
      NotificationHelper.createInfoNotification("Authentication Token copied to clipboard");
    }

    async function saveRecord() {
      try {
        switch (props.adminMode) {
          case "Add User":
            NotificationHelper.createNotification("Creating User...");
            await axios.get(`${process.env.VUE_APP_API_GATEWAY}createUserAndClaim?email=${email.value}&fullName=${encodeURIComponent(name.value)}&accessLevel=${accessLevel.value}&scopesAccess=${scopesAccess.value}&currentUserUID=${props.currentUserUID}&ipAddress=${props.ipAddress}&key=${process.env.VUE_APP_API_KEY}`);
            await axios.get(`${process.env.VUE_APP_API_GATEWAY}saveUserGroupEmails?email=${email.value}&userGroupUidList=${userGroupUidList.value}&currentUserUID=${props.currentUserUID}&ipAddress=${props.ipAddress}&key=${process.env.VUE_APP_API_KEY}`);
            props.userGroupsTable.getUserGroups();
            props.usersTable.getUsers();
            NotificationHelper.createSuccessNotification("User Created Successfully");
            closeForm();
            break;
          case "Update User":
            NotificationHelper.createNotification("Updating User...");
            await axios.get(`${process.env.VUE_APP_API_GATEWAY}updateUserInformation?email=${email.value}&fullName=${encodeURIComponent(name.value)}&accessLevel=${accessLevel.value}&scopesAccess=${scopesAccess.value}&currentUserUID=${props.currentUserUID}&ipAddress=${props.ipAddress}&key=${process.env.VUE_APP_API_KEY}`);
            await axios.get(`${process.env.VUE_APP_API_GATEWAY}saveUserGroupEmails?email=${email.value}&userGroupUidList=${userGroupUidList.value}&currentUserUID=${props.currentUserUID}&ipAddress=${props.ipAddress}&key=${process.env.VUE_APP_API_KEY}`);
            props.userGroupsTable.getUserGroups();
            props.usersTable.getUsers();
            NotificationHelper.createSuccessNotification("User Updated Successfully");
            closeForm();
            break;
          case "Add User Group":
            NotificationHelper.createNotification("Creating User Group...");
            await axios.get(`${process.env.VUE_APP_API_GATEWAY}saveUserGroup?name=${encodeURIComponent(name.value)}&accessLevel=${accessLevel.value}&scopesAccess=${scopesAccess.value}&emails=${emails.value}&currentUserUID=${props.currentUserUID}&ipAddress=${props.ipAddress}&key=${process.env.VUE_APP_API_KEY}`);
            props.userGroupsTable.getUserGroups();
            NotificationHelper.createSuccessNotification("User Group Created Successfully");
            closeForm();
            break;
          case "Update User Group":
            NotificationHelper.createNotification("Updating User Group...");
            await axios.get(`${process.env.VUE_APP_API_GATEWAY}saveUserGroup?uid=${userGroupUid.value}&name=${encodeURIComponent(name.value)}&accessLevel=${accessLevel.value}&scopesAccess=${scopesAccess.value}&emails=${emails.value}&currentUserUID=${props.currentUserUID}&ipAddress=${props.ipAddress}&key=${process.env.VUE_APP_API_KEY}`);
            props.userGroupsTable.getUserGroups();
            NotificationHelper.createSuccessNotification("User Group Updated Successfully");
            closeForm();
            break;
        }
      } catch (error) {
        EmailService.sendErrorEmail("AdminForm.vue - saveRecord", error);
        NotificationHelper.createErrorNotification("Error encountered. Please try again.");
      }
    }

    function closeForm() {
      if (props.adminMode === "Add User Group" || props.adminMode === "Update User Group") {
        emit("setAdminMode", "User Group List");
      } else {
        emit("setAdminMode", "User List");
      }
    }

    return {
      //variable properties
      displayAdminAccessLevel,
      email,
      name,
      accessLevel,
      scopesAccess,
      userGroups,
      users,
      authenticationToken,
      userGroupsOptions,
      usersOptions,
      disabledFields,

      //functions
      filterUserGroups,
      filterUsers,
      addRow,
      editRow,
      isValidEmail,
      isValidName,
      generateAuthenticationToken,
      copyAuthenticationTokenToClipboard,
      saveRecord,
      closeForm
    };
  },
};
</script>

<style scoped>
.inputLabel {
  font-size: 22px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica,
    Arial, sans-serif;
}
</style>